import { alignmentToString, stringToAlignment } from './../utils/commonFunctions';
import { applyRatio, unapplyRatio } from './../view_2/helpers';
import FontAttrsArray, { Alignment } from "../../model/FontAttrsArray"
import Color from '../../model/Color';
import { Editor } from '@tiptap/react';

export type TipTapParagraph = {
  type: string
  content: {
    type: string, 
    text: string, 
    marks: {type: string, attrs: {
      fontColor: string,
      fontSize: string,
      fontFamily: string,
    }}[]
  }[],
  attrs?: {textAlign: "left" | "right" | "center"}
}

export type TipTapDoc = {
  type: "doc",
  content: TipTapParagraph[]
}

export function flbArrayAsContent(array: FontAttrsArray[], text: string, ratio: number): TipTapDoc{
  const attrsWithSplitText = splitByNewline(constructSubstrings(array, text), ratio)
  const doc: TipTapDoc = {
    type: "doc",
    content: attrsWithSplitText
  }
  return doc
}

export const constructSubstrings = (array: FontAttrsArray[], text: string) => {
  return array.map((attrArray) => {
    let newAttr = Object.create(FontAttrsArray.prototype);
    Object.assign(newAttr, attrArray, {
        text: text.substring(attrArray.range[0], attrArray.range[0] + attrArray.range[1])
    });
    return newAttr
  })
}

type NewLineSplit = FontAttrsArray & {text: string}

export function splitByNewline(array: NewLineSplit[], ratio: number): TipTapParagraph[] {
  const newArray: NewLineSplit[][] = []
  const alignments: Alignment[] = []
  let newArrIndex = 0

  array.forEach((attr, index) => {
    // Check for newlines in ANY element, including the first one
    if (attr.text.includes("\n")) {
      const splitOnNewLine = attr.text.split(/(\n)/)
      splitOnNewLine.forEach((line, lineIndex) => {
        if (line === "\n") {
          // Start a new paragraph for the newline
          if (newArray.length === 0) {
            // Edge case: text starts with newline
            newArray.push([])
          }
          newArray.push([])
          newArrIndex = newArray.length - 1
        } else if (line.length === 0) {
          // Skip empty strings from split
        } else {
          let newAttr = Object.create(FontAttrsArray.prototype);
          Object.assign(newAttr, attr, {
            text: line
          });
          // Initialize first array if needed
          if (newArray.length === 0) {
            newArray.push([])
            newArrIndex = 0
          }
          alignments[newArrIndex] = newAttr.alignment
          newArray[newArrIndex].push(newAttr)
        }
      })
    } else {
      // No newlines - add to current or new paragraph based on alignment
      if (newArray.length === 0) {
        alignments.push(attr.alignment!)
        newArray.push([attr])
        newArrIndex = 0
      } else if (alignments[newArrIndex] !== attr.alignment) {
        newArrIndex = newArray.length
        alignments[newArrIndex] = attr.alignment!
        newArray.push([attr])
      } else {
        newArray[newArrIndex].push(attr)
      }
    }
  })

  return newArray.map((arr) => AttrArrayAsContent(arr, ratio))
}

export function AttrArrayAsContent(array: NewLineSplit[], ratio: number): TipTapParagraph {
  return {
    type: "paragraph",
    content: array.map((arr) => stylingAsContent(arr,ratio)),
    attrs: {
      textAlign: alignmentToString(array[0] && array[0].alignment ? array[0].alignment : Alignment.LEFT)
    }
  }
}

export const stylingAsContent = (attrArray: NewLineSplit, ratio: number) => {
  return {
    type: "text",
    text: attrArray.text,
    marks: [
      {
        type: "textStyle",
        attrs: {
          fontFamily: attrArray.name,
          fontSize: `${applyRatio({size: attrArray.size}, ratio).size}px`,
          fontColor: attrArray.color!.asRgba()
        }
      }
    ]
  }
}

/*
["Alignment:0|Color:000000ff|Name:HelveticaNeue-Light|Size:48.000000|Range:{0, 1}","Alignment:0|Color:000000ff|Name:HelveticaNeue-Light|Size:40.000000|Range:{1, 3}","Alignment:0|Color:000000ff|Name:HelveticaNeue-CondensedBold|Size:40.000000|Range:{4, 1}","Alignment:0|Color:000000ff|Name:HelveticaNeue-Light|Size:40.000000|Range:{5, 2}","Alignment:0|Color:000000ff|Name:HelveticaNeue-Light|Size:40.000000|Range:{7, 14}","Alignment:0|Color:f7931eff|Name:HelveticaNeue-Light|Size:40.000000|Range:{21, 2}"]
*/

export function contentAsAttrArray(content: TipTapDoc, ratio: number): {fontAttributes: FontAttrsArray[], text: string} {
  // Build text by joining paragraph contents with newlines
  const text = content.content.flatMap((paragraph) => {
    if (Array.isArray(paragraph.content)) {
      const paragraphText = paragraph.content.map(({text}) => text).join("")
      return paragraphText
    } else {
      // Empty/null paragraph content - contributes empty string, join adds newline
      return ""
    }
  }).join("\n")  // Join with newlines to preserve line breaks between paragraphs

  let index = 0;
  const paragraphCount = content.content.length;
  const step = content.content.map((paragraph, paragraphIndex) => {
    if (Array.isArray(paragraph.content)) {
      const [style, newIndex] = paragraphToArrays(paragraph, index, ratio)
      index = newIndex  // newIndex is absolute position, not delta
      // Account for newline after this paragraph (except for last paragraph)
      if (paragraphIndex < paragraphCount - 1) {
        index += 1
      }
      return style.map((s) => {
        s.alignment = stringToAlignment(paragraph.attrs?.textAlign || "left")
        return s
      })
    } else {
      // Empty paragraph - still need to account for newline (if not last)
      if (paragraphIndex < paragraphCount - 1) {
        index += 1
      }
    }
  })
  let fontAttributes: FontAttrsArray[] = step
    .filter((element) => element !== undefined) as unknown as FontAttrsArray[]
  fontAttributes = fontAttributes
    .flat()
    .reduce<FontAttrsArray[]>(reduceFontArray, [])
  return {
    fontAttributes,
    text
  }
} 

export function reduceFontArray(accumulator: FontAttrsArray[], current: FontAttrsArray | undefined): FontAttrsArray[] {
  if (!current) return accumulator
  if (accumulator.length === 0) return [current]
  let lastIndex = accumulator.length - 1
  let last = accumulator[accumulator.length - 1]
  if (current.equalsIgnoringRange(last)) {
    const newArr = accumulator.slice()
    // Calculate merged length: from start of first range to end of current range
    // This correctly handles gaps (like newlines) between ranges
    const endPosition = current.range[0] + current.range[1]
    newArr[lastIndex].range[1] = endPosition - newArr[lastIndex].range[0]
    return newArr
  }
  return [...accumulator, current]
}

export function paragraphToArrays(paragraph: TipTapParagraph, index: number, ratio: number): [FontAttrsArray[], number] {
  let localIndex = index;
  return [paragraph.content.map((content) => {
    let alignment = stringToAlignment(paragraph.attrs?.textAlign || "left")
    const attrs = content.marks[0].attrs
    let color = Color.fromRgbaString(attrs.fontColor)
    let size = unapplyRatio({size: parseFloat(attrs.fontSize.replace("px",""))}, ratio).size
    let name = attrs.fontFamily
    const {__typeFace, __typeStyle} = nameToFamilyAndStyle(name)
    let range: [number,number] = [localIndex, content.text.length]
    localIndex += content.text.length
    return new FontAttrsArray(alignment,color,name,size,range,__typeFace,__typeStyle)
  }), localIndex]
}

export const nameToFamilyAndStyle = (name: string) => {
  const split = name.split("-")  
  if (split.length > 1) {
    return {__typeFace: split[0], __typeStyle: split[1]}
  } else {
    return {__typeFace: name, __typeStyle: ""}
  }
}

export function editWrap(globalEditorVal: Editor, callback: (editor: Editor) => void): void {
  const wasEditableBeforeCall = !globalEditorVal.isEditable
  if (wasEditableBeforeCall) {
      globalEditorVal.setEditable(true)
  }
  callback(globalEditorVal)
  if (wasEditableBeforeCall) {
      globalEditorVal.setEditable(false)
  }
}